if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.defaultSubmitLabel = this.submitButtonText.textContent.trim();
        this.addedStateTimer = null;

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.querySelector('[data-retry-gift]')?.addEventListener('click', () => this.retryGift());
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => this.addGift(response))
          .then((response) => this.applyWelcomeOffer(response))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCartAfterAdd(response, variantId);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCartAfterAdd(response, variantId);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      renderCartAfterAdd(response, variantId) {
        // Open the cart (gift, total, Checkout and wallets in view) unless the
        // gift failed: then the shopper stays on the form, where the retry is.
        this.cart.renderContents(response, { open: !response.giftWarning });
        this.showAddedState();
        this.showGiftNotice(response);
        window.dispatchEvent(
          new CustomEvent('aleona:cart-added', {
            detail: { productVariantId: variantId, cart: response.welcomeCart },
          })
        );
      }

      showAddedState() {
        window.clearTimeout(this.addedStateTimer);
        this.submitButton.classList.add('product-form__submit--added');
        this.submitButtonText.textContent = window.variantStrings.addedToCart;

        this.addedStateTimer = window.setTimeout(() => {
          this.submitButton.classList.remove('product-form__submit--added');
          if (!this.error && !this.submitButton.querySelector('.sold-out-message:not(.hidden)')) {
            this.submitButtonText.textContent = this.defaultSubmitLabel || window.variantStrings.addToCart;
          }
        }, 1800);
      }

      // Gift with purchase (tw_gift_offer block): adds the gift line once per
      // cart after the product itself was added. The price is made zero by an
      // automatic Buy X Get Y discount in Shopify Admin, not here.
      async addGift(added) {
        const giftId = Number(this.dataset.giftVariant);
        if (added.status || !giftId) return added;
        try {
          const root = window.Shopify.routes.root;
          const currentResponse = await fetch(root + 'cart.js', { signal: AbortSignal.timeout(6000) });
          if (!currentResponse.ok) return this.markGiftWarning(added);
          const current = await currentResponse.json();
          if (current.items?.some((item) => item.variant_id === giftId)) return added;
          const properties = { '_ALEONA offer': this.dataset.giftOffer };
          // Gift on backorder: a visible line property, so the order shows it ships later.
          if (this.dataset.giftDispatch) properties[this.dataset.giftDispatchLabel] = this.dataset.giftDispatch;
          const response = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            signal: AbortSignal.timeout(6000),
            body: JSON.stringify({
              items: [{ id: giftId, quantity: 1, properties }],
              sections: this.cart?.getSectionsToRender().map((section) => section.id),
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) return this.markGiftWarning(added);
          const updated = await response.json();
          if (updated.sections && Object.values(updated.sections).every(Boolean)) added.sections = updated.sections;
          return added;
        } catch {
          return this.markGiftWarning(added);
        }
      }

      markGiftWarning(added) {
        added.giftWarning = true;
        return added;
      }

      showGiftNotice(response) {
        const notice = this.querySelector('[data-gift-notice]');
        if (!notice) return;
        this.lastGiftResponse = response;
        notice.hidden = !response.giftWarning;
        const message = notice.querySelector('[data-gift-notice-text]');
        if (message) message.textContent = response.giftWarning ? this.dataset.giftWarning : '';
      }

      async retryGift() {
        const retry = this.querySelector('[data-retry-gift]');
        if (!retry || retry.disabled) return;
        retry.disabled = true;
        retry.textContent = this.dataset.giftRetrying;
        const response = { ...(this.lastGiftResponse || {}) };
        delete response.giftWarning;
        try {
          // Retry only the gift. Never add the purchased bag a second time.
          await this.addGift(response);
          await this.applyWelcomeOffer(response);
          if (this.cart) {
            const sections = this.cart.getSectionsToRender().map(section => section.id).join(',');
            const fresh = await fetch(window.location.pathname + '?sections=' + encodeURIComponent(sections), { signal: AbortSignal.timeout(6000) });
            if (fresh.ok) {
              response.sections = await fresh.json();
              this.cart.renderContents(response, { open: false });
            }
          }
        } catch {
          response.giftWarning = true;
        } finally {
          this.showGiftNotice(response);
          retry.disabled = false;
          retry.textContent = this.dataset.giftRetry;
        }
      }

      async applyWelcomeOffer(added) {
        if (added.status || !this.dataset.welcomeCode) return added;
        try {
          const root = window.Shopify.routes.root;
          const currentResponse = await fetch(root + 'cart.js', { signal: AbortSignal.timeout(6000) });
          if (!currentResponse.ok) return added;
          const current = await currentResponse.json();
          added.welcomeCart = current;
          const giftLine = current.items?.find(item =>
            item.variant_id === Number(this.dataset.giftVariant) && item.final_line_price === 0
          );
          const onlyGiftDiscount = giftLine && current.total_discount === giftLine.original_line_price;
          if (current.discount_codes?.some(code => code.applicable) ||
              (current.total_discount > 0 && !onlyGiftDiscount)) return added;
          const response = await fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: AbortSignal.timeout(6000),
            body: JSON.stringify({
              discount: this.dataset.welcomeCode,
              sections: this.cart?.getSectionsToRender().map(section => section.id),
              sections_url: window.location.pathname
            })
          });
          if (!response.ok) return added;
          const updated = await response.json();
          const line = updated.items?.find(item => item.variant_id === Number(added.variant_id || added.id));
          if (updated.sections && Object.values(updated.sections).every(Boolean)) {
            if (line) added.key = line.key;
            added.sections = updated.sections;
          }
          added.welcomeCart = updated;
          return added;
        } catch {
          return added;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.defaultSubmitLabel = text || this.dataset.giftLabel || window.variantStrings.addToCart;
          this.submitButtonText.textContent = this.defaultSubmitLabel;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}


